import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer, Legend } from 'recharts';

// Calculate standard deviation ellipse parameters
const calculateDispersionEllipse = (shots) => {
  if (shots.length < 2) return null;

  // Calculate centroid (mean)
  const meanX = shots.reduce((sum, s) => sum + s.x, 0) / shots.length;
  const meanY = shots.reduce((sum, s) => sum + s.y, 0) / shots.length;

  // Calculate standard deviations
  const varianceX = shots.reduce((sum, s) => sum + Math.pow(s.x - meanX, 2), 0) / shots.length;
  const varianceY = shots.reduce((sum, s) => sum + Math.pow(s.y - meanY, 2), 0) / shots.length;
  const stdX = Math.sqrt(varianceX);
  const stdY = Math.sqrt(varianceY);

  // Calculate covariance for rotation
  const covariance = shots.reduce((sum, s) => sum + (s.x - meanX) * (s.y - meanY), 0) / shots.length;

  // Calculate rotation angle (principal axis)
  let angle = 0;
  if (Math.abs(covariance) > 0.01) {
    angle = 0.5 * Math.atan2(2 * covariance, varianceX - varianceY);
  }

  return {
    centerX: meanX,
    centerY: meanY,
    stdX,
    stdY,
    angle,
    // Dispersion metric: average of X and Y standard deviations
    dispersion: (stdX + stdY) / 2,
  };
};

// Generate ellipse SVG path string
const generateEllipsePath = (cx, cy, rx, ry, angle, sigma = 1) => {
  const radiusX = rx * sigma;
  const radiusY = ry * sigma;
  const numPoints = 64;

  let path = '';
  for (let i = 0; i <= numPoints; i++) {
    const theta = (i / numPoints) * 2 * Math.PI;
    // Ellipse in standard position
    const x = radiusX * Math.cos(theta);
    const y = radiusY * Math.sin(theta);
    // Rotate by angle
    const xRotated = x * Math.cos(angle) - y * Math.sin(angle);
    const yRotated = x * Math.sin(angle) + y * Math.cos(angle);
    // Translate to center
    const px = cx + xRotated;
    const py = cy + yRotated;

    if (i === 0) {
      path += `M ${px} ${py}`;
    } else {
      path += ` L ${px} ${py}`;
    }
  }
  path += ' Z'; // Close path
  return path;
};

// Custom component to render ellipses using chart coordinates
const DispersionEllipses = ({ scatterData, xScale, yScale }) => {
  if (!xScale || !yScale) return null;

  return (
    <g className="dispersion-ellipses">
      {scatterData.map((group, idx) => {
        if (!group.ellipse) return null;
        const { ellipse, color } = group;

        // Convert data coordinates to pixel coordinates
        const centerX = xScale(ellipse.centerX);
        const centerY = yScale(ellipse.centerY);

        // Calculate pixel-space radii by measuring scale
        const stdXPixels = Math.abs(xScale(ellipse.centerX + ellipse.stdX) - centerX);
        const stdYPixels = Math.abs(yScale(ellipse.centerY + ellipse.stdY) - centerY);

        // Generate paths in pixel coordinates
        const path1Sigma = generateEllipsePath(centerX, centerY, stdXPixels, stdYPixels, ellipse.angle, 1);
        const path2Sigma = generateEllipsePath(centerX, centerY, stdXPixels, stdYPixels, ellipse.angle, 2);

        return (
          <g key={`ellipse-${group.id}-${idx}`}>
            {/* 2-sigma ellipse (95%) - dashed, lighter */}
            <path
              d={path2Sigma}
              fill={group.color}
              fillOpacity={0.05}
              stroke={group.color}
              strokeWidth={2}
              strokeDasharray="5 5"
            />

            {/* 1-sigma ellipse (68%) - solid, darker */}
            <path
              d={path1Sigma}
              fill={group.color}
              fillOpacity={0.15}
              stroke={group.color}
              strokeWidth={2.5}
            />

            {/* Centroid marker (cross) */}
            <g>
              <line
                x1={centerX - 8}
                y1={centerY}
                x2={centerX + 8}
                y2={centerY}
                stroke={group.color}
                strokeWidth={3}
              />
              <line
                x1={centerX}
                y1={centerY - 8}
                x2={centerX}
                y2={centerY + 8}
                stroke={group.color}
                strokeWidth={3}
              />
              <circle
                cx={centerX}
                cy={centerY}
                r={4}
                fill="white"
                stroke={group.color}
                strokeWidth={2}
              />
            </g>
          </g>
        );
      })}
    </g>
  );
};

export const DispersionChart = React.memo(function DispersionChart({ shotGroups, distanceType = 'carry', selectedShot = null }) {
  // Memoize expensive calculations
  const chartData = useMemo(() => {
    const allIncludedShots = shotGroups.flatMap(g => g.shots.filter(s => s.included));

    if (allIncludedShots.length === 0) {
      return null;
    }

    // TrackMan-style dispersion: X = distance (carry or total), Y = offline
    const scatterData = shotGroups.map(group => {
      const shotData = group.shots
        .filter(shot => shot.included)
        .map(shot => ({
          x: distanceType === 'carry' ? (shot.metrics.carry || 0) : (shot.metrics.total || 0), // X-axis = Distance
          y: shot.metrics.offlineDistance || 0, // Y-axis = Offline (centered at 0)
          shotNumber: shot.shotNumber,
          groupName: group.name,
          groupId: group.id,
          isSelected: selectedShot?.groupId === group.id && selectedShot?.shotNumber === shot.shotNumber,
        }));

      // Calculate dispersion ellipse
      const ellipse = calculateDispersionEllipse(shotData);

      return {
        id: group.id,
        name: group.name,
        color: group.color,
        data: shotData,
        ellipse, // Add dispersion ellipse data
      };
    }).filter(g => g.data.length > 0);

    // Find distance range for X-axis based on distance type
    const maxDistance = Math.max(...allIncludedShots.map(s =>
      distanceType === 'carry' ? (s.metrics.carry || 0) : (s.metrics.total || 0)
    ), 0);

    return { scatterData, maxDistance };
  }, [shotGroups, distanceType, selectedShot]);

  // Empty state handling
  if (!chartData) {
    return (
      <div className="flex items-center justify-center h-[500px] text-zinc-600 bg-zinc-900/50 rounded-lg border border-zinc-800">
        <div className="text-center">
          <p className="text-lg font-semibold mb-2 text-zinc-400">No dispersion data available</p>
          <p className="text-sm text-zinc-500">Upload shots to see shot dispersion pattern</p>
        </div>
      </div>
    );
  }

  const { scatterData, maxDistance } = chartData;

  // Calculate distance intervals for reference lines (every 50 yards)
  const distanceMarkers = [];
  const maxRounded = Math.ceil(maxDistance / 50) * 50 + 50;
  for (let dist = 50; dist <= maxRounded; dist += 50) {
    distanceMarkers.push(dist);
  }

  return (
    <ResponsiveContainer width="100%" height={500}>
      <ScatterChart margin={{ top: 20, right: 30, left: 60, bottom: 40 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />

        {/* X-axis = Distance (TrackMan style) */}
        <XAxis
          type="number"
          dataKey="x"
          domain={[0, maxRounded]}
          name="Distance"
          label={{ value: `Distance - ${distanceType === 'carry' ? 'Carry' : 'Total'} (yards)`, position: 'insideBottom', offset: -15, fill: '#a1a1aa' }}
          stroke="#71717a"
          tick={{ fill: '#a1a1aa' }}
        />

        {/* Y-axis = Offline, centered at 0 (TrackMan style) */}
        <YAxis
          type="number"
          dataKey="y"
          domain={[-50, 50]}
          name="Offline"
          label={{ value: 'Offline (yards)', angle: -90, position: 'insideLeft', fill: '#a1a1aa' }}
          stroke="#71717a"
          tick={{ fill: '#a1a1aa' }}
        />

        <Tooltip
          cursor={{ strokeDasharray: '3 3' }}
          contentStyle={{
            backgroundColor: '#18181b',
            border: '1px solid #52525b',
            borderRadius: '8px',
            padding: '10px 14px',
            color: '#fafafa',
            boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
          }}
          labelStyle={{ color: '#a1a1aa', fontWeight: 600, marginBottom: 4 }}
          itemStyle={{ color: '#fafafa' }}
          formatter={(value, name) => {
            if (name === 'x') return [`${value.toFixed(1)} yards`, 'Distance'];
            if (name === 'y') return [`${value > 0 ? '+' : ''}${value.toFixed(1)} yards`, 'Offline'];
            return value;
          }}
        />

        <Legend
          verticalAlign="top"
          height={36}
          iconType="circle"
          wrapperStyle={{ paddingBottom: '10px', color: '#fafafa' }}
        />

        {/* Center line at Y=0 (fairway center) — solid line for clear left/right reference */}
        <ReferenceLine y={0} stroke="#a1a1aa" strokeWidth={2} label={{ value: '0 yds', fill: '#a1a1aa', fontSize: 12, position: 'right' }} />

        {/* Distance interval markers (every 50 yards) */}
        {distanceMarkers.map(dist => (
          <ReferenceLine
            key={dist}
            x={dist}
            stroke="#27272a"
            strokeWidth={1}
            strokeDasharray="2 2"
          />
        ))}

        {/* Custom layer for dispersion ellipses - renders after grid/axes but before scatter points */}
        <Scatter
          data={[]}
          shape={(props) => {
            // Use the first Scatter to inject custom SVG with access to scales
            const { xAxis, yAxis } = props;
            if (!xAxis || !yAxis) return null;

            // Get scale functions from axes
            const xScale = xAxis.scale;
            const yScale = yAxis.scale;

            return <DispersionEllipses scatterData={scatterData} xScale={xScale} yScale={yScale} />;
          }}
          isAnimationActive={false}
        />

        {/* Scatter series for each shot group */}
        {scatterData.map(group => (
          <Scatter
            key={group.id}
            name={`${group.name} (${group.ellipse ? `±${group.ellipse.dispersion.toFixed(1)} yds` : ''})`}
            data={group.data}
            fill={group.color}
            shape="circle"
          />
        ))}
      </ScatterChart>
    </ResponsiveContainer>
  );
});

DispersionChart.propTypes = {
  shotGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      shots: PropTypes.arrayOf(
        PropTypes.shape({
          included: PropTypes.bool.isRequired,
          shotNumber: PropTypes.number.isRequired,
          metrics: PropTypes.shape({
            carry: PropTypes.number,
            total: PropTypes.number,
            offlineDistance: PropTypes.number,
          }).isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  distanceType: PropTypes.oneOf(['carry', 'total']),
  selectedShot: PropTypes.shape({
    groupId: PropTypes.string,
    shotNumber: PropTypes.number,
  }),
};
